// File: frontend/src/hooks/usePersonRoles.ts
//
// Fetches the active person-role list once per app session and caches it.
// Components that need the role list (dropdown, translate, icon) all share
// the same cached result — no duplicate network calls.

import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { MdPersonRoleDto, getPublicPersonRoles } from "../services/masterDataService";

// Module-level cache — survives re-renders and re-mounts
let cachedRoles: MdPersonRoleDto[] | null = null;
let fetchPromise: Promise<MdPersonRoleDto[]> | null = null;

/** Fetch active roles, using cached result if already loaded. */
const fetchRoles = (): Promise<MdPersonRoleDto[]> => {
  if (cachedRoles) return Promise.resolve(cachedRoles);
  if (fetchPromise) return fetchPromise;
  fetchPromise = getPublicPersonRoles().then(roles => {
    cachedRoles = roles;
    fetchPromise = null;
    return roles;
  });
  return fetchPromise;
};

/** Invalidate the cache — call after admin creates/updates/toggles a role. */
export const invalidatePersonRoleCache = () => {
  cachedRoles = null;
  fetchPromise = null;
};

/** Affiliation kinds a role can declare — mirrors md_person_role.affiliation_type. */
export type RoleAffiliationType = "CLUB" | "ORGANIZATION" | "NONE";

interface UsePersonRolesResult {
  roles: MdPersonRoleDto[];
  loading: boolean;
  /** Translate a role code to the display name in the current language. */
  translateRole: (code: string) => string;
  /** Get the iconKey for a role code, falling back to "FaUser". */
  getIconKey: (code: string) => string;
  /**
   * Affiliation kind for a role code. Unknown roles and roles saved before the
   * affiliation_type column existed fall back to "CLUB", the previous behaviour.
   */
  getAffiliationType: (code: string) => RoleAffiliationType;
}

export const usePersonRoles = (): UsePersonRolesResult => {
  const { i18n } = useTranslation();
  const [roles, setRoles] = useState<MdPersonRoleDto[]>(cachedRoles ?? []);
  const [loading, setLoading] = useState(!cachedRoles);

  useEffect(() => {
    if (cachedRoles) {
      setRoles(cachedRoles);
      setLoading(false);
      return;
    }
    let mounted = true;
    setLoading(true);
    fetchRoles()
      .then(r => { if (mounted) { setRoles(r); setLoading(false); } })
      .catch(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  const translateRole = (code: string): string => {
    const role = roles.find(r => r.code === code);
    if (!role) return code;
    return i18n.language.startsWith("tr") ? role.nameTr : role.nameEn;
  };

  const getIconKey = (code: string): string => {
    const role = roles.find(r => r.code === code);
    return role?.iconKey ?? "FaUser";
  };

  const getAffiliationType = (code: string): RoleAffiliationType => {
    const role = roles.find(r => r.code === code);
    const value = role?.affiliationType;
    if (value === "ORGANIZATION" || value === "NONE") return value;
    return "CLUB";
  };

  return { roles, loading, translateRole, getIconKey, getAffiliationType };
};